import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import MainLayout from '../../components/layout/MainLayout';
import {
  Users, Briefcase, FileText, TrendingUp,
  Shield, Settings, Lock, CheckCircle2,
  Activity, Loader2, UserPlus
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [statsData, users] = await Promise.all([
          adminApi.getStats(),
          adminApi.getAllUsers('limit=100'),
        ]);
        setStats(statsData);
        const sorted = [...users].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setRecentUsers(sorted.slice(0, 5));
      } catch (err) {
        toast.error(err.message || 'Failed to load admin dashboard');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const adminModules = [
    { id: 'users', label: 'User Management', desc: 'Manage all platform users', icon: Users, color: 'blue', path: '/admin/users', count: stats?.users?.total },
    { id: 'access', label: 'Page Access Manager', desc: 'Control page permissions', icon: Lock, color: 'purple', path: '/admin/access', count: null },
    { id: 'cases', label: 'All Cases', desc: 'View & manage all cases', icon: Briefcase, color: 'green', path: '/admin/cases', count: stats?.cases?.total },
    { id: 'clients', label: 'All Clients', desc: 'View & manage all clients', icon: Users, color: 'orange', path: '/admin/clients', count: stats?.clients?.total },
    { id: 'documents', label: 'All Documents', desc: 'View all documents', icon: FileText, color: 'cyan', path: '/admin/documents', count: stats?.documents?.total },
    { id: 'analytics', label: 'Platform Analytics', desc: 'Usage & performance', icon: TrendingUp, color: 'pink', path: '/admin/analytics', count: null },
    { id: 'settings', label: 'Platform Settings', desc: 'Configure platform', icon: Settings, color: 'gray', path: '/admin/settings', count: null },
  ];

  const quickStats = [
    { label: 'Total Users', value: stats?.users?.total || 0, icon: Users, color: 'blue', sub: `${stats?.users?.active || 0} active` },
    { label: 'Total Cases', value: stats?.cases?.total || 0, icon: Briefcase, color: 'green', sub: `${stats?.cases?.active || 0} active` },
    { label: 'Total Clients', value: stats?.clients?.total || 0, icon: Users, color: 'orange', sub: 'across all users' },
    { label: 'Documents', value: stats?.documents?.total || 0, icon: FileText, color: 'purple', sub: 'uploaded' },
  ];

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return iso;
    }
  };

  if (loading) {
    return (
      <MainLayout title="Admin Dashboard" subtitle="Platform Administration">
        <div className="flex items-center justify-center py-32" data-testid="admin-dashboard-loading">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Admin Dashboard" subtitle="Platform Administration">
      <div data-testid="admin-dashboard">
        {/* Admin Badge */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 p-6 rounded-2xl relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.95) 0%, rgba(59,130,246,0.95) 100%)' }}
        >
          <motion.div
            className="absolute top-0 right-0 w-64 h-64 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)' }}
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 10, repeat: Infinity }}
          />
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-2xl bg-white/20 backdrop-blur-sm">
                <Shield className="w-10 h-10 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Administrator Access</h2>
                <p className="text-white/70">Full control over all platform data and settings</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-white/20 text-white">
                <Activity className="w-5 h-5" />
              </div>
              <div className="text-right text-white">
                <p className="text-sm opacity-70">System Status</p>
                <p className="font-semibold flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  All Systems Operational
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-6 mb-8">
          {quickStats.map((stat, index) => (
            <motion.div
              key={stat.label}
              data-testid={`admin-stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02, y: -5 }}
              className="glass-card p-6 relative overflow-hidden"
            >
              <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-${stat.color}-500 to-${stat.color}-400`} />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                  <h3 className="text-3xl font-bold mt-1">{Number(stat.value).toLocaleString()}</h3>
                  <p className="text-xs text-gray-400 mt-1">{stat.sub}</p>
                </div>
                <div className={`p-3 rounded-xl bg-${stat.color}-500/20`}>
                  <stat.icon className={`w-6 h-6 text-${stat.color}-500`} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Admin Modules Grid */}
        <h3 className="text-lg font-semibold mb-4">Admin Modules</h3>
        <div className="grid grid-cols-4 gap-6">
          {adminModules.map((module, index) => (
            <motion.div
              key={module.id}
              data-testid={`admin-module-${module.id}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.05 }}
              whileHover={{ scale: 1.02, y: -5 }}
              onClick={() => navigate(module.path)}
              className="glass-card p-6 cursor-pointer relative overflow-hidden group"
            >
              <motion.div className={`absolute inset-0 bg-gradient-to-r from-${module.color}-500/10 to-${module.color}-500/5 opacity-0 group-hover:opacity-100 transition-opacity`} />
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-xl bg-${module.color}-500/20`}>
                    <module.icon className={`w-6 h-6 text-${module.color}-500`} />
                  </div>
                  {module.count != null && (
                    <span className="text-sm font-semibold text-gray-500">{Number(module.count).toLocaleString()}</span>
                  )}
                </div>
                <h4 className="font-semibold text-lg mb-1">{module.label}</h4>
                <p className="text-sm text-gray-500">{module.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Recently Registered Users */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold mb-4">Recently Registered Users</h3>
          <div className="glass-card p-6" data-testid="admin-recent-users">
            {recentUsers.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">No users registered yet.</p>
            ) : (
              <div className="space-y-4">
                {recentUsers.map((u, index) => (
                  <motion.div
                    key={u.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + index * 0.08 }}
                    className="flex items-center justify-between p-4 rounded-xl bg-gray-500/5"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${u.role === 'admin' ? 'bg-purple-500/20 text-purple-500' : 'bg-blue-500/20 text-blue-500'}`}>
                        {u.role === 'admin' ? <Shield className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                      </div>
                      <div>
                        <p className="font-medium">{u.full_name}</p>
                        <p className="text-sm text-gray-500">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`text-xs px-2 py-1 rounded-full capitalize flex items-center gap-1 ${u.status === 'active' ? 'bg-green-500/20 text-green-500' : 'bg-gray-500/20 text-gray-500'}`}>
                        <CheckCircle2 className="w-3 h-3" />{u.status}
                      </span>
                      <span className="text-sm text-gray-400">{formatDate(u.created_at)}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AdminDashboard;
